/**
 * components/eventDate.js — date affichée sur la page publique d'un événement.
 *
 * Règle métier : un événement qui ne tient pas dans une seule journée doit
 * s'annoncer comme une PÉRIODE ("Du dimanche 20 septembre au mardi 22 septembre
 * 2026") et non par son seul jour de début, qui laissait croire à un événement
 * d'une journée.
 *
 * ⚠️ `dateFin` n'a PAS le même sens selon la famille d'événement (cf.
 * services/events_public.js côté api) :
 *   - kind "PE" (Parcours d'événement, le nouveau système) : dateEvent/dateFin
 *     sont info.startEventDate/endEventDate → ce sont bien le début et la FIN DE
 *     L'ÉVÉNEMENT. C'est le seul cas où la période a un sens.
 *   - kind "AFFICHE" (collection `events`, legacy) : dateFin est la fin de la
 *     FENÊTRE D'AFFICHAGE de l'affiche sur le site, sans rapport avec la durée de
 *     l'événement (une affiche du 20 septembre peut rester visible jusqu'au 30).
 *     L'afficher en période mentirait → on garde la date seule.
 *
 * Les sessions d'un groupe multi-session n'ont pas de `kind` (elles ne passent pas
 * par la vue unifiée) mais portent `newDesign: true`, posé par buildPeSession :
 * on accepte donc les deux marqueurs.
 *
 * Localisation : Intl (comme lib/shabbatDateRange.js), pas dayjs — la locale vient
 * du contexte de la page, alors que dayjs.locale() est un réglage global.
 */

import { t } from "@lingui/core/macro";

// Le code interne "gr" n'est pas une locale BCP-47 valide.
function intlLocale(locale) {
    return locale === "gr" ? "el" : (locale || "fr");
}

function toDate(value) {
    if (!value) return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
}

// Comparaison sur le jour LOCAL, celui qu'on affiche.
function isSameDay(a, b) {
    return a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

// Une date sans heure saisie arrive à minuit : on ne l'affiche pas dans la période.
function hasTime(d) {
    return d.getHours() !== 0 || d.getMinutes() !== 0;
}

/**
 * Fin D'ÉVÉNEMENT exploitable, ou null quand `dateFin` ne la porte pas (affiche).
 */
export function getEventEndDate(event) {
    if (!event) return null;
    const isPe = event.kind === "PE" || event.newDesign === true;
    if (!isPe) return null;
    return toDate(event.dateFin);
}

/**
 * `true` quand l'événement s'étale sur plusieurs jours (donc affiché en période).
 */
export function isMultiDayEvent(event) {
    const start = toDate(event?.dateEvent);
    const end = getEventEndDate(event);
    if (!start || !end) return false;
    if (end.getTime() <= start.getTime()) return false;
    if (event.annualEventTimeZone) {
        const day = new Intl.DateTimeFormat('en-CA', {timeZone:event.annualEventTimeZone, year:'numeric',month:'2-digit',day:'2-digit'});
        return day.format(start) !== day.format(end);
    }
    return !isSameDay(start, end);
}

/**
 * Date d'un événement, en jour unique ou en période.
 *
 * @param {object} event   événement (ou session PE) tel que renvoyé par /v1/events
 * @param {string} locale  locale de la page ("fr", "en", "he"…)
 * @param {object} [opts]
 * @param {boolean} [opts.withTime=true]  afficher l'heure sur un événement d'un jour
 * @returns {string} "" si la date de début est absente/invalide
 */
export function formatEventDate(event, locale, { withTime = true } = {}) {
    const start = toDate(event?.dateEvent);
    if (!start) return "";

    const loc = intlLocale(locale);
    const zone = event?.annualEventTimeZone ? {timeZone:event.annualEventTimeZone} : {};
    const time = { hour: "2-digit", minute: "2-digit", ...zone };
    const full = { weekday: "long", day: "numeric", month: "long", year: "numeric", ...zone };

    if (!isMultiDayEvent(event)) {
        return new Intl.DateTimeFormat(loc, withTime ? { ...full, ...time } : full).format(start);
    }

    const end = getEventEndDate(event);
    // L'année n'est portée que par la borne de fin quand les deux la partagent :
    // "Du dimanche 20 septembre au mardi 22 septembre 2026".
    const sameYear = start.getFullYear() === end.getFullYear();
    const startOpts = sameYear ? { weekday: "long", day: "numeric", month: "long", ...zone } : full;

    const startText = new Intl.DateTimeFormat(
        loc,
        event?.annualEventTimeZone || hasTime(start) ? { ...startOpts, ...time } : startOpts,
    ).format(start);
    const endText = new Intl.DateTimeFormat(
        loc,
        event?.annualEventTimeZone || hasTime(end) ? { ...full, ...time } : full,
    ).format(end);

    return t`Du ${startText} au ${endText}`;
}

export default formatEventDate;
